import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createLiveEvent, updateLiveEvent, deleteLiveEvent } from '../services/api';
import axios from 'axios';
import QuotaDisplay from './QuotaDisplay';
import QuotaErrorBoundary from './QuotaErrorBoundary';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TwitterIcon from '@mui/icons-material/Twitter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';

interface LiveEvent {
  id?: number;
  title: string;
  venue: string;
  start_at: string;
  open_at: string | null;
  publish_at: string | null;
  status: string;
  ticket_sales_status?: string | null;
  ticket_types?: string | null;
  ticket_sales_start?: string | null;
  door_open_time?: string | null;
  performance_start_time?: string | null;
  special_event_info?: string | null;
  special_instructions?: string | null;
  reference_url?: string | null;
}

interface ExtractedData {
  date: string;
  venue: string;
  event_type: string;
  title: string;
  confidence: number;
}

interface AdditionalInfo {
  ticket_sales_status?: string;
  ticket_types?: string;
  ticket_sales_start?: string;
  door_open_time?: string;
  performance_start_time?: string;
  special_event_info?: string;
  special_instructions?: string;
  reference_url?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const LiveEventManage: React.FC = () => {
  const { token, refreshTokenFunction } = useAuth();
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<LiveEvent | null>(null);
  const [formData, setFormData] = useState<LiveEvent>({
    title: '',
    venue: '',
    start_at: '',
    open_at: null,
    publish_at: null,
    status: 'draft'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [tweetText, setTweetText] = useState('');
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [additionalInfo, setAdditionalInfo] = useState<AdditionalInfo | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');
  const [useAI, setUseAI] = useState(false);
  const [extractMethod, setExtractMethod] = useState<'gemini' | 'regex' | 'unknown'>('unknown');
  const [extractMessage, setExtractMessage] = useState('');
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  const API_URL = 'https://mokalive.net:8000/api';

  const fetchEvents = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/live-events/manage/`, {
        headers: { Authorization: `JWT ${token}` }
      });
      setEvents(response.data);
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        try {
          const newToken = await refreshTokenFunction();
          if (newToken) {
            const retryResponse = await axios.get(`${API_URL}/live-events/manage/`, {
              headers: { Authorization: `JWT ${newToken}` }
            });
            setEvents(retryResponse.data);
          }
        } catch (refreshError) {}
      }
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [token]);

  const handleOpenDialog = (event?: LiveEvent) => {
    if (event) {
      setEditingEvent(event);
      setFormData(event);
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        venue: '',
        start_at: '',
        open_at: null,
        publish_at: null,
        status: 'draft'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setError('');
  };

  const handlePinSubmit = () => {
    // PIN: 0808
    const CORRECT_PIN = '0808';
    
    if (pinInput === CORRECT_PIN) {
      setUseAI(true);
      setPinDialogOpen(false);
      setPinInput('');
      setPinError('');
    } else {
      setPinError('PINが正しくありません。');
      setPinInput('');
    }
  };

  const handleSubmit = async () => {
    if (!token) return;

    try {
      if (editingEvent && editingEvent.id) {
        await updateLiveEvent(token, editingEvent.id, formData);
        setSuccess('更新しました');
      } else {
        await createLiveEvent(token, formData);
        setSuccess('作成しました');
      }
      
      handleCloseDialog();
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('保存に失敗しました');
    }
  };

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm('本当に削除しますか？')) return;

    try {
      await deleteLiveEvent(token, id);
      setSuccess('削除しました');
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('削除に失敗しました');
    }
  };

  // X ツイートテキストから情報を抽出
  const handleExtractFromTwitter = async () => {
    if (!tweetText.trim()) {
      setExtractError('ツイートテキストを入力してください');
      return;
    }

    if (!token) return;

    setExtracting(true);
    setExtractError('');
    setExtractedData(null);
    setAdditionalInfo(null);
    setExtractMethod('unknown');
    setExtractMessage('');

    try {
      const response = await axios.post(
        `${API_URL}/live-events/extract/`,
        { 
          tweet_text: tweetText,
          use_ai: useAI,  // AI使用オプション
          ai_pin: useAI ? '0808' : null  // AI使用時はPINを送信
        },
        { headers: { Authorization: `JWT ${token}` } }
      );

      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        console.log('📡 API レスポンス:', response.data);
        console.log('📋 additional_info:', response.data.additional_info);
        console.log('📋 reference_url (top level):', response.data.reference_url);
        
        // 追加情報を統合（reference_url がトップレベルにある場合とadditional_info内にある場合の両対応）
        let mergedAdditionalInfo = response.data.additional_info ? { ...response.data.additional_info } : {};
        if (response.data.reference_url) {
          mergedAdditionalInfo.reference_url = response.data.reference_url;
        }
        
        if (Object.keys(mergedAdditionalInfo).length > 0) {
          console.log('✓ 追加情報を設定:', mergedAdditionalInfo);
          setAdditionalInfo(mergedAdditionalInfo);
        } else {
          console.log('⚠️ 追加情報がレスポンスに含まれていません');
        }
        const message: string = response.data.message || '';
        const method = response.data.extraction_method
          ? response.data.extraction_method
          : message.includes('gemini')
            ? 'gemini'
            : message.includes('regex')
              ? 'regex'
              : 'unknown';
        setExtractMethod(method);
        setExtractMessage(message);
      } else {
        setExtractError(response.data.error || '抽出に失敗しました');
      }
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        try {
          const newToken = await refreshTokenFunction();
          if (newToken) {
            const retryResponse = await axios.post(
              `${API_URL}/live-events/extract/`,
              { 
                tweet_text: tweetText,
                use_ai: useAI,  // AI使用オプション
                ai_pin: useAI ? '0808' : null  // AI使用時はPINを送信
              },
              { headers: { Authorization: `JWT ${newToken}` } }
            );
            
            if (retryResponse.data.success) {
              setExtractedData(retryResponse.data.extracted_data);
              console.log('📡 リトライ API レスポンス:', retryResponse.data);
              console.log('📋 additional_info:', retryResponse.data.additional_info);
              console.log('📋 reference_url (top level):', retryResponse.data.reference_url);
              
              // 追加情報を統合（reference_url がトップレベルにある場合とadditional_info内にある場合の両対応）
              let mergedAdditionalInfo = retryResponse.data.additional_info ? { ...retryResponse.data.additional_info } : {};
              if (retryResponse.data.reference_url) {
                mergedAdditionalInfo.reference_url = retryResponse.data.reference_url;
              }
              
              if (Object.keys(mergedAdditionalInfo).length > 0) {
                console.log('✓ 追加情報を設定:', mergedAdditionalInfo);
                setAdditionalInfo(mergedAdditionalInfo);
              } else {
                console.log('⚠️ 追加情報がレスポンスに含まれていません');
              }
              const message: string = retryResponse.data.message || '';
              const method = retryResponse.data.extraction_method
                ? retryResponse.data.extraction_method
                : message.includes('gemini')
                  ? 'gemini'
                  : message.includes('regex')
                    ? 'regex'
                    : 'unknown';
              setExtractMethod(method);
              setExtractMessage(message);
            } else {
              setExtractError(retryResponse.data.error || '抽出に失敗しました');
            }
          }
        } catch (refreshError) {
          setExtractError('認証に失敗しました');
        }
      } else {
        setExtractError(err.response?.data?.error || '抽出に失敗しました');
      }
    } finally {
      setExtracting(false);
    }
  };

  // 抽出したデータをフォームに反映
  const handleUseExtractedData = () => {
    if (!extractedData) return;

    // datetime-local形式に変換 (YYYY-MM-DDTHH:mm)
    let formattedDate = '';
    if (extractedData.date) {
      try {
        const dateObj = new Date(extractedData.date);
        // タイムゾーンを考慮してローカル時刻に変換
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (e) {
        console.error('日付変換エラー:', e);
      }
    }

    // 追加情報を整形
    let ticketTypesStr = '';
    let ticketSalesStartStr = '';
    let doorOpenTimeStr = '';
    let performanceStartTimeStr = '';

    if (additionalInfo) {
      // チケット種別をテキスト化
      if (additionalInfo.ticket_types) {
        try {
          let parsed: any = additionalInfo.ticket_types;
          // JSON文字列の場合はパース
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
          
          // JSONオブジェクトの場合の処理
          if (typeof parsed === 'object' && parsed !== null) {
            if (parsed.tickets && Array.isArray(parsed.tickets)) {
              // 複数チケットの場合
              ticketTypesStr = parsed.tickets
                .map((t: any) => `${t.name}: ${t.price}`)
                .join(' / ');
            } else if (parsed.name && parsed.price) {
              // 単一チケットの場合
              ticketTypesStr = `${parsed.name}: ${parsed.price}`;
            }
          } else {
            // 文字列の場合
            ticketTypesStr = String(parsed);
          }
        } catch (e) {
          // JSON解析失敗時は文字列として使用
          ticketTypesStr = String(additionalInfo.ticket_types);
        }
      }

      // 販売開始をdatetime形式に
      if (additionalInfo.ticket_sales_start) {
        try {
          const salesDate = new Date(additionalInfo.ticket_sales_start);
          const year = salesDate.getFullYear();
          const month = String(salesDate.getMonth() + 1).padStart(2, '0');
          const day = String(salesDate.getDate()).padStart(2, '0');
          const hours = String(salesDate.getHours()).padStart(2, '0');
          const minutes = String(salesDate.getMinutes()).padStart(2, '0');
          ticketSalesStartStr = `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (e) {
          console.error('販売開始日時変換エラー:', e);
        }
      }

      // 入場時刻
      doorOpenTimeStr = additionalInfo.door_open_time || '';

      // 開演時刻
      performanceStartTimeStr = additionalInfo.performance_start_time || '';
    }

    setFormData({
      title: extractedData.title,
      venue: extractedData.venue,
      start_at: formattedDate,
      open_at: null,
      publish_at: new Date().toISOString(),
      status: 'draft',
      ticket_sales_status: additionalInfo?.ticket_sales_status || undefined,
      ticket_types: ticketTypesStr || undefined,
      ticket_sales_start: ticketSalesStartStr || undefined,
      door_open_time: doorOpenTimeStr || undefined,
      performance_start_time: performanceStartTimeStr || undefined,
      special_event_info: additionalInfo?.special_event_info || undefined,
      special_instructions: additionalInfo?.special_instructions || undefined,
      reference_url: additionalInfo?.reference_url || undefined,
    });

    setEditingEvent(null);
    setOpenDialog(true);
    setTabValue(0); // 通常入力タブに戻す
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{
      pt: { xs: 7, sm: 8 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '80vh',
      px: { xs: 1, sm: 2 }
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: 600 },
        pb: 2
      }}>
        <Typography variant="h6" gutterBottom align="center" sx={{
          mb: 3,
          fontSize: '1.3rem',
          fontWeight: 'bold',
          color: '#d81b60',
          letterSpacing: 1,
          textShadow: '0 2px 8px rgba(216, 27, 96, 0.1)'
        }}>
          ライブイベント管理
        </Typography>

        <Box display="flex" justifyContent="center" gap={1} mb={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditingEvent(null); setFormData({
              title: '',
              venue: '',
              start_at: '',
              open_at: null,
              publish_at: null,
              status: 'draft'
            }); setTabValue(0); setOpenDialog(true); }}
            sx={{
              background: 'linear-gradient(135deg, #d81b60 0%, #f06292 100%)',
              color: '#fff',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(216, 27, 96, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #c2185b 0%, #ec407a 100%)',
              }
            }}
          >
            新規作成
          </Button>
          <Button
            variant="outlined"
            startIcon={<TwitterIcon />}
            onClick={() => { setEditingEvent(null); setFormData({
              title: '',
              venue: '',
              start_at: '',
              open_at: null,
              publish_at: null,
              status: 'draft'
            }); setTabValue(1); setOpenDialog(true); }}
            sx={{
              borderColor: '#1DA1F2',
              color: '#1DA1F2',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'rgba(29, 161, 242, 0.1)',
              }
            }}
          >
            Xから抽出
          </Button>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{
          p: 2,
          background: 'linear-gradient(135deg, #fff1f8 0%, #f8bbd0 100%)',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(255, 128, 171, 0.08)',
          border: '1.5px solid #f8bbd0'
        }}>
          <List>
        {events.map((event: any) => (
          <ListItem
            key={event.id}
            divider
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: 1,
              mb: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.8)'
              }
            }}
            secondaryAction={
              <Box>
                <IconButton 
                  onClick={() => handleOpenDialog(event)}
                  sx={{ color: '#d81b60' }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  onClick={() => handleDelete(event.id)}
                  sx={{ color: '#d32f2f' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            }
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1">{event.title}</Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: event.status === 'published' ? 'success.light' : 'grey.300',
                      color: event.status === 'published' ? 'success.dark' : 'text.secondary'
                    }}
                  >
                    {event.status === 'published' ? '公開' : event.status === 'draft' ? '下書き' : 'アーカイブ'}
                  </Typography>
                </Box>
              }
              secondary={
                <>
                  <Typography variant="body2" component="span" display="block">
                    会場: {event.venue}
                  </Typography>
                  <Typography variant="body2" component="span" display="block">
                    開催: {format(new Date(event.start_at), 'yyyy/MM/dd HH:mm')}
                  </Typography>
                  {event.status === 'published' && event.publish_at && (
                    <Typography variant="body2" component="span" display="block" color="text.secondary">
                      公開予約: {format(new Date(event.publish_at), 'yyyy/MM/dd HH:mm')}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
          </List>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #d81b60 0%, #f06292 100%)',
          color: '#fff',
          fontWeight: 'bold'
        }}>
          {editingEvent ? 'ライブイベント編集' : '新規ライブイベント作成'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="イベント入力方法">
            <Tab label="手動入力" id="event-tab-0" />
            <Tab label="X/チケットサイト" icon={<TwitterIcon />} id="event-tab-1" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="イベント名"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="開催場所"
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="開催日時"
                type="datetime-local"
                value={formData.start_at}
                onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={formData.status}
                label="ステータス"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="draft">下書き</MenuItem>
                <MenuItem value="published">公開</MenuItem>
                <MenuItem value="archived">アーカイブ</MenuItem>
              </Select>
            </FormControl>
            {formData.status === 'published' && (
              <TextField
                label="公開予約日時"
                type="datetime-local"
                value={formData.publish_at || ''}
                onChange={(e) => setFormData({ ...formData, publish_at: e.target.value || null })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="この日時になると自動的に公開されます（未設定の場合は即座に公開）"
              />
            )}

            {/* 追加情報セクション */}
            <Box sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              border: '1px solid #1976d2',
              borderRadius: 1
            }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                📋 追加情報（任意）
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                チケットサイトから抽出した情報を入力・編集できます
              </Typography>

              <TextField
                label="チケット販売状況"
                value={formData.ticket_sales_status || ''}
                onChange={(e) => setFormData({ ...formData, ticket_sales_status: e.target.value })}
                fullWidth
                placeholder="例: 販売中、売切れ"
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="チケット情報"
                value={formData.ticket_types || ''}
                onChange={(e) => setFormData({ ...formData, ticket_types: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="例: 前売り 2,500円 / 当日 3,000円"
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="販売開始"
                type="datetime-local"
                value={formData.ticket_sales_start || ''}
                onChange={(e) => setFormData({ ...formData, ticket_sales_start: e.target.value || undefined })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="入場開始"
                type="time"
                value={formData.door_open_time || ''}
                onChange={(e) => setFormData({ ...formData, door_open_time: e.target.value || undefined })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="特典会情報"
                value={formData.special_event_info || ''}
                onChange={(e) => setFormData({ ...formData, special_event_info: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="例: 特典会 12月31日 15:00～ SHIBUYA VIDENT"
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="当日の注意事項"
                value={formData.special_instructions || ''}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="例: 身分証をお持ちください。未就学児入場不可。撮影禁止。"
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="参考URL"
                value={formData.reference_url || ''}
                onChange={(e) => setFormData({ ...formData, reference_url: e.target.value })}
                fullWidth
                placeholder="例: https://tiget.net/events/444360"
                sx={{ mb: 1.5 }}
              />
            </Box>
          </Box>

          <Box sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderLeft: '4px solid #d81b60',
            borderRadius: 1
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#d81b60', fontWeight: 'bold' }}>
              📋 イベント登録手順
            </Typography>
            <Box sx={{ mb: 2, fontSize: '0.875rem' }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                <strong>1.</strong> 基本情報を入力（イベント名、開催場所、開催日時は必須）
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                <strong>2.</strong> ステータスを選択（公開/下書き/アーカイブ）
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                <strong>3.</strong> 「公開」を選択した場合、公開予約日時を設定可能（任意）
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#d81b60', fontWeight: 'bold' }}>
              🏷️ ステータスの違い
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'grey.300',
                    color: 'text.secondary',
                    fontWeight: 'bold',
                    minWidth: '60px',
                    textAlign: 'center',
                    fontSize: '0.7rem'
                  }}
                >
                  下書き
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  準備中。ユーザーには<strong>非表示</strong>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'success.light',
                    color: 'success.dark',
                    fontWeight: 'bold',
                    minWidth: '60px',
                    textAlign: 'center',
                    fontSize: '0.7rem'
                  }}
                >
                  公開
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  ユーザーに<strong>表示</strong>（公開予約日時で表示タイミング調整可）
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'grey.300',
                    color: 'text.secondary',
                    fontWeight: 'bold',
                    minWidth: '60px',
                    textAlign: 'center',
                    fontSize: '0.7rem'
                  }}
                >
                  アーカイブ
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  過去イベント。ユーザーには<strong>非表示</strong>
                </Typography>
              </Box>
            </Box>
          </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info">
                ✅ X（Twitter）のツイートテキスト<br/>
                ✅ tiget.net などのチケットサイトURL<br/>
                を貼り付けると、日付・会場・イベント名を自動抽出します。
              </Alert>

              <TextField
                label="ツイートテキストまたはチケットサイトURL"
                multiline
                rows={4}
                value={tweetText}
                onChange={(e) => setTweetText(e.target.value)}
                placeholder="例1: 2月8日(日)原宿GE Theaterにて生誕祭開催！&#10;例2: https://tiget.net/events/444360"
                fullWidth
              />

              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <input
                  type="checkbox"
                  checked={useAI}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // AI有効化時は PIN 入力ダイアログを表示
                      setPinDialogOpen(true);
                      setPinInput('');
                      setPinError('');
                    } else {
                      // AI無効化
                      setUseAI(false);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  disabled={false}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  AI抽出を使用（より柔軟な表現に対応）
                  <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5 }}>
                    チェックを入れると、標準方式での抽出に失敗した場合に Google Gemini AI での抽出を試みます。
                    {useAI && <Typography variant="caption" sx={{ display: 'block', color: '#f44336', fontWeight: 'bold', mt: 0.3 }}>
                      ※ API無料枠が超過している場合は失敗します。その場合は手動で入力してください。
                    </Typography>}
                  </Typography>
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleExtractFromTwitter}
                disabled={!tweetText.trim() || extracting}
                sx={{
                  background: 'linear-gradient(135deg, #1DA1F2 0%, #1a8cd8 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover:not(:disabled)': {
                    background: 'linear-gradient(135deg, #1a8cd8 0%, #1678c2 100%)',
                  },
                  '&:disabled': {
                    background: '#ccc'
                  }
                }}
              >
                {extracting ? <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} /> : null}
                {extracting ? '抽出中...' : '情報を抽出'}
              </Button>

              {/* API 無料枠表示（折りたたみ可能） */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#999', mb: 1, fontWeight: 'bold' }}>
                  📊 API使用状況（折りたたみ表示）
                </Typography>
                <QuotaErrorBoundary>
                  <QuotaDisplay refreshInterval={30000} showDetails={false} />
                </QuotaErrorBoundary>
              </Box>

              {extractError && (
                <Alert severity="error">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {extractError}
                  </Typography>
                  <Typography variant="caption">
                    対応方法：
                    <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                      <li>X: 日付を「MM月DD日」形式で含める（例: 「2月8日」「02/08」）</li>
                      <li>チケットサイト: tiget.net のイベントページURLを直接貼り付け</li>
                      <li>AI抽出を OFF にして手動入力タブで直接入力</li>
                      <li>AI抽出ON時はAPIクォータ超過の可能性を確認</li>
                    </ul>
                  </Typography>
                </Alert>
              )}

              {extractedData && (
                <Card sx={{
                  backgroundColor: 'rgba(29, 161, 242, 0.05)',
                  border: '2px solid #1DA1F2',
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        抽出成功！
                      </Typography>
                      <Chip
                        label={
                          extractMethod === 'gemini'
                            ? 'AI使用: あり'
                            : extractMethod === 'regex'
                              ? 'AI使用: なし'
                              : 'AI使用: 不明'
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: extractMethod === 'gemini' ? '#1DA1F2' : '#9e9e9e',
                          color: extractMethod === 'gemini' ? '#1DA1F2' : '#616161'
                        }}
                      />
                      <Chip
                        label={`信頼度: ${(extractedData.confidence * 100).toFixed(0)}%`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: extractedData.confidence > 0.8 ? '#4caf50' : '#ff9800',
                          color: extractedData.confidence > 0.8 ? '#4caf50' : '#ff9800'
                        }}
                      />
                    </Box>

                    {extractMessage && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                        {extractMessage}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          イベント名
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {extractedData.title}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          開催日時
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {format(new Date(extractedData.date), 'yyyy/MM/dd HH:mm')}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          会場
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {extractedData.venue}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          イベントタイプ
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {extractedData.event_type}
                        </Typography>
                      </Box>

                      {/* 追加情報の表示 */}
                      {additionalInfo && Object.keys(additionalInfo).length > 0 && (
                        <>
                          <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5, mt: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              📋 追加情報（オプション）
                            </Typography>
                          </Box>

                          {additionalInfo.ticket_sales_status && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                チケット販売状況
                              </Typography>
                              <Typography variant="body2">
                                {additionalInfo.ticket_sales_status}
                              </Typography>
                            </Box>
                          )}

                          {additionalInfo.ticket_types && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                チケット情報
                              </Typography>
                              <Typography variant="body2">
                                {typeof additionalInfo.ticket_types === 'string'
                                  ? additionalInfo.ticket_types
                                  : JSON.stringify(additionalInfo.ticket_types, null, 2)}
                              </Typography>
                            </Box>
                          )}

                          {additionalInfo.ticket_sales_start && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                販売開始
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(additionalInfo.ticket_sales_start), 'yyyy/MM/dd HH:mm')}
                              </Typography>
                            </Box>
                          )}

                          {additionalInfo.door_open_time && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                入場開始
                              </Typography>
                              <Typography variant="body2">
                                {additionalInfo.door_open_time}
                              </Typography>
                            </Box>
                          )}

                          {additionalInfo.special_event_info && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                特典会情報
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {additionalInfo.special_event_info}
                              </Typography>
                            </Box>
                          )}

                          {additionalInfo.special_instructions && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                ⚠️ 当日の注意事項
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#e53935', fontWeight: 'bold' }}>
                                {additionalInfo.special_instructions}
                              </Typography>
                            </Box>
                          )}

                          {additionalInfo.reference_url && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                参考URL
                              </Typography>
                              <Typography variant="body2">
                                <Link href={additionalInfo.reference_url} target="_blank" rel="noopener">
                                  {additionalInfo.reference_url}
                                </Link>
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleUseExtractedData}
                      sx={{
                        mt: 2,
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        color: '#fff',
                        fontWeight: 'bold',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #45a049 0%, #5cb85c 100%)',
                        }
                      }}
                    >
                      この情報を使用して作成
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          {tabValue === 0 && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.title || !formData.venue || !formData.start_at}
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                color: '#fff',
                fontWeight: 'bold',
                '&:hover:not(:disabled)': {
                  background: 'linear-gradient(135deg, #45a049 0%, #5cb85c 100%)',
                },
                '&:disabled': {
                  background: '#ccc'
                }
              }}
            >
              {editingEvent ? '更新' : '作成'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* PIN 入力ダイアログ */}
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)}>
        <DialogTitle>AI抽出機能を有効化</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            AI抽出機能を有効にするには、PINコードを入力してください。
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="PINコード"
            value={pinInput}
            onChange={(e) => {
              setPinInput(e.target.value);
              setPinError('');
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePinSubmit();
              }
            }}
            error={!!pinError}
            helperText={pinError}
            placeholder="4桁のPINを入力"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => {
            setPinDialogOpen(false);
            setPinInput('');
            setPinError('');
          }}>
            キャンセル
          </Button>
          <Button 
            onClick={handlePinSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              }
            }}
          >
            確認
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveEventManage;
